// hooks/usePWAInstall.js
import { useEffect, useState, useCallback } from "react";
import { onInstallAvailable, getDeferredPrompt } from "@helpers/pwaInstall";

function isStandalone() {
    return (
        window.matchMedia?.("(display-mode: standalone)")?.matches ||
        window.navigator.standalone === true // iOS
    );
}
function isIOS() {
    const ua = window.navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export default function usePWAInstall({ onIOSFallback } = {}) {
    const [available, setAvailable] = useState(!!getDeferredPrompt());
    const [installing, setInstalling] = useState(false);
    const [installed, setInstalled] = useState(isStandalone());

    useEffect(() => {
        const off = onInstallAvailable(() => setAvailable(true));
        const onAppInstalled = () => {
            setInstalled(true);
            setAvailable(false);
        };
        window.addEventListener("appinstalled", onAppInstalled);
        return () => {
            off?.();
            window.removeEventListener("appinstalled", onAppInstalled);
        };
    }, []);

    const install = useCallback(async () => {
        const promptEvent = getDeferredPrompt();
        if (!promptEvent) {
            // iOS no tiene beforeinstallprompt: abre tu fallback si lo definiste
            onIOSFallback?.();
            return;
        }
        try {
            setInstalling(true);
            promptEvent.prompt();
            await promptEvent.userChoice; // outcome: 'accepted' | 'dismissed'
        } finally {
            setInstalling(false);
            // Tras usarlo, el navegador invalida el prompt; dejarás de mostrar el item
            setAvailable(false);
        }
    }, [onIOSFallback]);

    return {
        available: !installed && available,
        installing,
        installed,
        isIOS: isIOS(),
        install,
    };
}
